// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@store/MobiusOverrides/Button/Button";
import Typography, { TypographyProps } from "@store/MobiusOverrides/Typography/Typography";
import AppleStore from "@store/Library/Components/Icons/AppleStore";
import GooglePlay from "@store/Library/Components/Icons/GooglePlay";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    backgroundOne = "backgroundOne",
    titleOne = "titleOne",
    subtitleOne = "subtitleOne",
    destinationOne = "destinationOne",
    iconOne = "iconOne",
    appleOne = "appleOne",
    googleOne = "googleOne",
    imageOne = "imageOne",
    useHighLightTwo = "useHighLightTwo",
    backgroundTwo = "backgroundTwo",
    titleTwo = "titleTwo",
    subtitleTwo = "subtitleTwo",
    destinationTwo = "destinationTwo",
    iconTwo = "iconTwo",
    appleTwo = "appleTwo",
    googleTwo = "googleTwo",
    imageTwo = "imageTwo",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundOne]: "primary" | "secondary";
        [fields.titleOne]: string;
        [fields.subtitleOne]: string;
        [fields.destinationOne]: LinkFieldValue;
        [fields.iconOne]: string;
        [fields.appleOne]: LinkFieldValue;
        [fields.googleOne]: LinkFieldValue;
        [fields.imageOne]: string;
        [fields.useHighLightTwo]: boolean;
        [fields.backgroundTwo]: "primary" | "secondary";
        [fields.titleTwo]: string;
        [fields.subtitleTwo]: string;
        [fields.destinationTwo]: LinkFieldValue;
        [fields.iconTwo]: string;
        [fields.appleTwo]: LinkFieldValue;
        [fields.googleTwo]: LinkFieldValue;
        [fields.imageTwo]: string;
    };
}

export interface HighLightStyles {
    gridContainer?: GridContainerProps;
    highlightSubContainerTop?: GridContainerProps;
    highlightSubContainerBottom?: GridContainerProps;
    highlightContainer?: GridItemProps;
    highlightContent?: GridItemProps;
    title?: TypographyProps;
    subtitle?: TypographyProps;
    iconWrapper?: InjectableCss;
    icon?: ImgProps;
    textGridItem?: GridItemProps;
    video?: InjectableCss;
    buttonWrapper?: InjectableCss;
    highlightButtons?: GridItemProps;
}

const highlightSubContainer = css`
    position: relative;
    z-index: 2;
    display: flex;
`;

export const highLightStyles: HighLightStyles = {
    gridContainer: {
        gap: 24,
    },
    highlightContainer: {
        css: css`
            display: flex;
            width: 100%;
            height: 100%;
            padding: ${pxToRem(90, 60, 40, 60)};
            flex-direction: column;
            position: relative;
            &::before {
                content: "";
                display: block;
                position: absolute;
                z-index: 1;
                top: 0;
                right: 8%;
                bottom: 0;
                left: 25%;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: right;
            }
        `,
    },
    highlightSubContainerTop: {
        css: css`
            ${highlightSubContainer}
            align-items: space-between;
        `,
    },
    highlightSubContainerBottom: {
        css: css`
            ${highlightSubContainer}
            align-items: flex-end;
        `,
    },
    highlightContent: {
        width: [9, 9, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    title: {
        css: css`
            color: ${getColor("primary.contrast")};
        `,
    },
    subtitle: {
        css: css`
            color: ${getColor("primary.contrast")};
        `,
    },
    iconWrapper: {
        css: css`
            margin: ${pxToRem(30, 0)};
            display: block;
            width: 100%;
        `,
    },
    icon: {
        css: css`
            height: auto;
            max-width: 40%;
        `,
    },
    highlightButtons: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            width: 100%;
            justify-content: center;
            align-items: center;
            ${breakpoint("md", "up")} {
                justify-content: flex-start;
            }
            > div,
            > button {
                flex-grow: 1;
                margin-right: ${pxToRem(20)};
                width: ${pxToRem(119)};
            }
        `,
    },
    buttonWrapper: {
        css: css`
            position: relative;
            z-index: 3;
            display: inline-block;
            padding: 0;
            background: none;
            border: none;
            max-width: ${pxToRem(120)};
            margin-top: ${pxToRem(20)};
            &:hover {
                background: none;
                border: none;
            }
        `,
    },
};

type Props = OwnProps;

const styles = highLightStyles;

const HighLight: FC<Props> = ({ fields }) => {
    const links: {
        0: {
            destination: string | undefined;
            apple: string | undefined;
            google: string | undefined;
        };
        1: {
            destination: string | undefined;
            apple: string | undefined;
            google: string | undefined;
        };
    } = {
        0: {
            destination: useGetLink(fields.destinationOne).url,
            apple: useGetLink(fields.appleOne).url,
            google: useGetLink(fields.googleOne).url,
        },
        1: {
            destination: useGetLink(fields.destinationTwo).url,
            apple: useGetLink(fields.appleTwo).url,
            google: useGetLink(fields.googleTwo).url,
        },
    };

    const clickHandler = (index: number, link: string, target = "_self") => {
        type linkGroupType = {
            [key: string]: string | undefined;
        };
        const linkGroup: linkGroupType = links[index as keyof typeof links];
        const url = linkGroup[link];
        if (!url) {
            return;
        }
        window.open(url, target);
    };
    const highlightsArrayOne: (string | LinkFieldValue | boolean)[] = [];
    const highlightsArrayTwo: (string | LinkFieldValue | boolean)[] = [];
    Object.entries(fields).map(([key, value]) => {
        const thisKey: any = key.slice(0, -3);
        if (key.endsWith("One")) {
            highlightsArrayOne[thisKey] = value;
        }
        if (key.endsWith("Two")) {
            highlightsArrayTwo[thisKey] = value;
        }
        return value;
    });

    const highlightsArray: any[] = fields.useHighLightTwo
        ? [highlightsArrayOne, highlightsArrayTwo]
        : [highlightsArrayOne];

    const highlightContent = (
        highlight: {
            apple: LinkFieldValue;
            background: string;
            destination: LinkFieldValue;
            google: LinkFieldValue;
            icon: string;
            image: string;
            subtitle: string;
            title: string;
        },
        index: number,
    ) => {
        const { apple, background, destination, google, icon, image, subtitle, title } = highlight;

        const highlightContainerWrapper: GridItemProps = {
            width: fields.useHighLightTwo ? [12, 12, 12, 6, 6] : 12,
        };

        const highlightContainer: InjectableCss = {
            css: css`
                ${styles.highlightContainer?.css}
                background: linear-gradient(transparent 0%, transparent 10%, ${getColor(background)} 10%, ${getColor(
                    background,
                )} 100%);
                &::before {
                    background-image: url(${image});
                }
                ${destination && `cursor: pointer;`}
            `,
        };

        const storeButton = (index: number, type: "apple" | "google", storeUrl: LinkFieldValue | undefined) => {
            if (!storeUrl?.value) {
                return;
            }

            const buttonStyle: IconProps = {
                width: 119,
                height: 40,
                src: type === "apple" ? AppleStore : GooglePlay,
            };

            return (
                <Button
                    {...styles.buttonWrapper}
                    onClick={event => {
                        event.stopPropagation();
                        clickHandler(index, type, "_blank");
                    }}
                >
                    <Icon {...buttonStyle} />
                </Button>
            );
        };

        return (
            <GridItem key={index} {...highlightContainerWrapper}>
                <StyledWrapper
                    {...highlightContainer}
                    onClick={event => {
                        event.stopPropagation();
                        clickHandler(index, "destination");
                    }}
                >
                    <GridContainer {...styles.highlightSubContainerTop}>
                        <GridItem {...styles.highlightContent}>
                            {title && (
                                <Typography {...styles.title} variant="h2">
                                    {title}
                                </Typography>
                            )}
                            {subtitle && (
                                <Typography {...styles.subtitle} variant="p">
                                    {subtitle}
                                </Typography>
                            )}
                            {icon && (
                                <StyledWrapper {...styles.iconWrapper}>
                                    <Img src={icon} {...styles.icon} />
                                </StyledWrapper>
                            )}
                        </GridItem>
                    </GridContainer>

                    {(apple.value || google.value) && (
                        <GridContainer {...styles.highlightSubContainerBottom}>
                            <GridItem {...styles.highlightButtons}>
                                {storeButton(index, "apple", apple)}
                                {storeButton(index, "google", google)}
                            </GridItem>
                        </GridContainer>
                    )}
                </StyledWrapper>
            </GridItem>
        );
    };

    return (
        <GridContainer {...styles.gridContainer}>
            {highlightsArray.map((highlight, index) => {
                return highlightContent(highlight, index);
            })}
        </GridContainer>
    );
};
const widgetModule: WidgetModule = {
    component: HighLight,
    definition: {
        displayName: "Highlight",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.backgroundOne,
                displayName: "Background Colour | Highlight One",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Primary",
                        value: "primary",
                    },
                    {
                        displayName: "Secondary",
                        value: "secondary",
                    },
                ],
                defaultValue: "secondary",
                fieldType: "General",
            },
            {
                name: fields.titleOne,
                displayName: "Title | Highlight One",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.subtitleOne,
                displayName: "Subtitle | Highlight One",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.destinationOne,
                displayName: "Link | Highlight One",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: fields.iconOne,
                displayName: "Icon | Highlight One",
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.appleOne,
                displayName: "Apple Store Link | Highlight One",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "General",
            },
            {
                name: fields.googleOne,
                displayName: "Google Play Link | Highlight One",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "General",
            },
            {
                name: fields.imageOne,
                displayName: "Image | Highlight One",
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.useHighLightTwo,
                displayName: "Setup Second HighLight",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
            },
            {
                name: fields.backgroundTwo,
                displayName: "Background Colour | Highlight Two",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Primary",
                        value: "primary",
                    },
                    {
                        displayName: "Secondary",
                        value: "secondary",
                    },
                ],
                defaultValue: "secondary",
                fieldType: "General",
                isVisible: widget => widget.fields.useHighLightTwo,
            },
            {
                name: fields.titleTwo,
                displayName: "Title | Highlight Two",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                isVisible: widget => widget.fields.useHighLightTwo,
            },
            {
                name: fields.subtitleTwo,
                displayName: "Subtitle | Highlight Two",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.useHighLightTwo,
            },
            {
                name: fields.destinationTwo,
                displayName: "Link | Highlight Two",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isVisible: widget => widget.fields.useHighLightTwo,
            },
            {
                name: fields.iconTwo,
                displayName: "Icon | Highlight Two",
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.useHighLightTwo,
            },
            {
                name: fields.appleTwo,
                displayName: "Apple Store Link | Highlight Two",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "General",
                isVisible: widget => widget.fields.useHighLightTwo,
            },
            {
                name: fields.googleTwo,
                displayName: "Google Play Link | Highlight Two",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "General",
                isVisible: widget => widget.fields.useHighLightTwo,
            },
            {
                name: fields.imageTwo,
                displayName: "Image | Highlight Two",
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.useHighLightTwo,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
